import { ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { Tooltip } from '@/components/ui';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  subtitle?: string;
  color: 'emerald' | 'blue' | 'brand' | 'orange';
  isLoading?: boolean;
  onClick?: () => void;
  tooltip?: {
    title: string;
    description: string;
    formula?: string;
  };
}

export function StatCard({ title, value, icon: Icon, trend, subtitle, color, isLoading = false, onClick, tooltip }: StatCardProps) {
  const colorClasses = {
    emerald: 'from-emerald-500/20 to-emerald-600/20 text-emerald-400',
    blue: 'from-blue-500/20 to-blue-600/20 text-blue-400',
    brand: 'from-[#F78C4E]/20 to-[#E67A3C]/20 text-[#F78C4E]',
    orange: 'from-orange-500/20 to-orange-600/20 text-orange-400',
  };

  return (
    <div className={`rounded-xl border border-zinc-800 bg-zinc-900/50 p-6${onClick ? ' cursor-pointer transition-colors hover:border-zinc-700 hover:bg-zinc-900/80' : ''}`} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend && !isLoading && (
          <div className={`flex items-center gap-1 text-xs ${trend.isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend.isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {trend.value}%
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="mt-4 flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          <span className="text-sm text-zinc-500">Loading...</span>
        </div>
      ) : (
        <p className="mt-4 text-2xl font-bold text-white">{value}</p>
      )}
      <div className="mt-1 flex items-center">
        {tooltip ? (
          <Tooltip title={tooltip.title} description={tooltip.description} formula={tooltip.formula}>
            <span className="text-sm text-zinc-400">{title}</span>
          </Tooltip>
        ) : (
          <span className="text-sm text-zinc-400">{title}</span>
        )}
      </div>
      {subtitle && !isLoading && <p className="text-xs text-zinc-500">{subtitle}</p>}
    </div>
  );
}
